import * as moment from "moment";
import { z } from "zod";

import { isPostcodeValid, postcodeValidationErrorMessage } from "@teamprevent/common/validators/postcodeIsValid";
import { timeRegex } from "@teamprevent/common/simpleui-input/dates/timeRegex";

import { Location } from "redux-little-router";

export interface RoomListModel {
    readonly id: string;
    readonly name: string;
    readonly listUpdated: moment.Moment;
    readonly addressLine1: string | null;
    readonly addressLine2: string | null;
    readonly addressLine3: string | null;
    readonly city: string | null;
    readonly county: string | null;
    readonly postcode: string | null;
}

export interface RoomDetailModel extends RoomListModel {
    readonly notes: string | null;
    readonly customerNotes: string | null;
    readonly workingDayStartTime: string | null;
    readonly workingDayEndTime: string | null;
    readonly workingDays: number[] | null;
    readonly detailUpdated: moment.Moment;
    readonly position: [number, number] | null;
    readonly geocodeStatus: "Pending" | "Geocoded" | "No Matching Addresses" | string | null;
}

export type Room = RoomListModel | RoomDetailModel;

export interface RoomState {
    readonly rooms: Room[];
    readonly room?: Room;
}

export interface RoomFilter {
    name?: string;
    postcode?: string;
}

export interface RoomAndDistance {
    id: string;
    distance: number;
}

export interface RoomAndDistanceFailedGeocodeResult {
    geocodedSuccessful: false;
}

export interface RoomAndDistanceResult {
    geocodedSuccessful: true;
    roomsAndDistances: RoomAndDistance[];
}

export type AppState = RoomState & { router: Location };

export const roomCreateUpdateModelSchemaFactory = () => {
    return z.object({
        name: z.string({ required_error: "Name is required" }).min(1, "Name is required"),
        addressLine1: z.string({ required_error: "Address Line 1 is required" }).min(1, "Address Line 1 is required"),
        addressLine2: z.string().nullish().optional(),
        addressLine3: z.string().nullish().optional(),
        city: z.string().nullish().optional(),
        county: z.string().nullish().optional(),
        postcode: z.string({ required_error: "Postcode is required" })
            .min(1, "Postcode is required")
            .refine(isPostcodeValid, postcodeValidationErrorMessage),
        notes: z.string().nullish().optional(),
        customerNotes: z.string().nullish().optional(),
        workingDayStartTime: z.string({ required_error: "Working Day Start Time is required", })
            .min(1, "Working Day Start Time is required")
            .refine(time => time !== undefined && timeRegex.test(time), "Working Day Start Time is not a valid time"),
        workingDayEndTime: z.string({ required_error: "Working Day Finish Time is required" })
            .min(1, "Working Day Finish Time is required")
            .refine(time => time !== undefined && timeRegex.test(time), "Working Day Finish Time is not a valid time"),
        workingDays: z.array(z.number()),
    }).required();
};

export type RoomCreateUpdateModel = Required<z.infer<ReturnType<typeof roomCreateUpdateModelSchemaFactory>>>;
